/**
 * UpdateBanner — slim update-notification strip that lives in the AppShell header.
 *
 * Renders nothing while the app is idle / up-to-date.
 * Once an update is available it shows a non-intrusive pill the cashier can act on:
 *
 *   [available]    "v1.2.3 available — Download"            [×]
 *   [downloading]  "Downloading v1.2.3 … 42 %  ████▒▒▒▒"   (no close)
 *   [ready]        "v1.2.3 ready — Restart to install"      [×]
 *   [error]        "Update error — see Settings"            [×]
 *
 * The banner is dismissible (except during active download). A dismissed banner
 * will re-appear on the next `update-available` event (app restart cycle).
 *
 * Full update management (manual check, detailed progress) lives in SettingsScreen.
 */
import { useState } from 'react';
import { Download, RefreshCw, X, AlertCircle } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAppUpdater } from '@/hooks/useAppUpdater';

export function UpdateBanner() {
  const { status, updateInfo, downloadProgress, errorMessage, download, install } = useAppUpdater();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed when a new update cycle begins
  // (status going back to 'available' means a new version arrived)
  const visible =
    !dismissed &&
    (status === 'available' || status === 'downloading' || status === 'ready' || status === 'error');

  if (!visible) return null;

  const canDismiss = status !== 'downloading';

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-1 text-label',
        status === 'available'   && 'bg-accent/10 text-accent-ink',
        status === 'downloading' && 'bg-fg-tertiary/10 text-fg-secondary',
        status === 'ready'       && 'bg-green-500/10 text-green-700 dark:text-green-400',
        status === 'error'       && 'bg-red-500/10 text-red-600',
      )}
    >
      {/* Icon */}
      {status === 'available'   && <Download size={12} strokeWidth={2} />}
      {status === 'downloading' && <RefreshCw size={12} strokeWidth={2} className="animate-spin" />}
      {status === 'ready'       && <Download size={12} strokeWidth={2} />}
      {status === 'error'       && <AlertCircle size={12} strokeWidth={2} />}

      {/* Text + action */}
      {status === 'available' && (
        <>
          <span>v{updateInfo?.version} available —</span>
          <button
            type="button"
            onClick={download}
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            Download
          </button>
        </>
      )}
      {status === 'downloading' && (
        <>
          <span>Downloading v{updateInfo?.version}</span>
          {downloadProgress !== null && (
            <>
              <span className="tabular-nums">{Math.round(downloadProgress.percent ?? 0)}%</span>
              {/* Micro progress bar */}
              <div className="h-1.5 w-16 rounded-full bg-fg-tertiary/20 overflow-hidden">
                <div
                  className="h-full rounded-full bg-accent transition-all duration-200"
                  style={{ width: `${downloadProgress.percent ?? 0}%` }}
                />
              </div>
            </>
          )}
        </>
      )}
      {status === 'ready' && (
        <>
          <span>v{updateInfo?.version} ready —</span>
          <button
            type="button"
            onClick={install}
            className="font-medium underline underline-offset-2 hover:no-underline"
          >
            Restart to install
          </button>
        </>
      )}
      {status === 'error' && (
        <span>Update error{errorMessage ? ` — ${errorMessage}` : ''}</span>
      )}

      {/* Dismiss button */}
      {canDismiss && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss update banner"
          className="ml-1 rounded p-0.5 opacity-60 hover:opacity-100 transition-opacity"
        >
          <X size={11} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}
